import {
  CANDIDATE_FIELDS,
  CANDIDATE_SORTABLE,
  EMPTY_TABLE_STATE,
  FIELD_TYPE_CATALOGUE,
  completeFilter,
  encodeTableState,
  type FilterCondition,
  type TableState,
} from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Download, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BulkBar } from '../components/candidates/bulk-actions.tsx';
import { CandidateDetail } from '../components/candidates/candidate-detail.tsx';
import { EditableCell } from '../components/candidates/editable-cell.tsx';
import { HistoryPanel } from '../components/candidates/history-panel.tsx';
import { ResumeCell } from '../components/candidates/resume-cell.tsx';
import { CandidateForm } from '../components/candidates/candidate-form.tsx';
import { FilterControls } from '../components/record-table/filter-controls.tsx';
import {
  RecordTable,
  ViewsMenu,
  type ColumnDef,
  type RecordSource,
} from '../components/record-table/index.ts';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet.tsx';
import { describeError } from '../lib/api.ts';
import {
  api,
  type CandidateRecord,
  type CandidateInput,
  type CandidateSearchRequest,
  type FieldDefinitionView,
  type PipelineView,
} from '../lib/client.ts';
import { flagForCountry } from '../lib/countries.ts';
import { useSession } from '../lib/session.tsx';
import { useSettings } from '../lib/settings.ts';
import { useVisibleDefinitions } from '../lib/custom-fields.ts';
import { candidateFilterFields } from '../lib/list-filters.ts';
import { useListView } from '../lib/list-view.ts';
import { PageHeader } from '../components/ui/page-header.tsx';
import { AddToJobDialog } from '../components/projects/shortlist-dialogs.tsx';
import { describeAdd } from '../lib/shortlists.ts';
import { candidateStageOptions, usePipeline } from '../lib/record-data.ts';
import { HOURLY_RATE_FORMAT, dateTone, stageTone } from '../lib/value-tones.ts';
import { Input } from '../components/ui/input.tsx';

/**
 * Candidates on the real API (RELAY-941): columns, filters and the form all read the
 * organisation's field definitions, so a field added in Settings → Fields shows up here on
 * the next load with no deploy. Standard columns are a fixed set; custom ones are appended
 * from the definitions, rendered and edited by their type.
 */
const STANDARD_COLUMNS: Array<{
  key: keyof CandidateRecord;
  label: string;
  size: number;
  /** Inline-editable (RELAY-942); a plain input field, Stage a dropdown of the default pipeline's. */
  editable: boolean;
  /** RecordTable's content width: extra px for a pill or flag; `false` keeps `size`. */
  autoSize: false | number;
}> = [
  { key: 'currentRole', label: 'Role', size: 180, editable: true, autoSize: 0 },
  { key: 'country', label: 'Country', size: 130, editable: true, autoSize: 22 },
  { key: 'stage', label: 'Stage', size: 130, editable: true, autoSize: 24 },
  { key: 'expectedHourlyRate', label: 'Hourly Rate', size: 110, editable: true, autoSize: 24 },
  // Fixed: the table would measure the ISO value, longer than the date pill shown.
  { key: 'applicationDate', label: 'Application Date', size: 150, editable: true, autoSize: false },
  { key: 'createdAt', label: 'Creation Date', size: 130, editable: false, autoSize: false },
];

const DATE_PILLS = new Set<keyof CandidateRecord>(['applicationDate', 'createdAt']);

const candidateName = (c: Pick<CandidateRecord, 'firstName' | 'lastName'>) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ');

// The Name column sorts by first name: a search takes one sort key.
const apiSortField = (id: string) => (id === 'name' ? 'firstName' : id);

// A stage the Candidate Flow pipeline does not list stays a neutral pill.
const candidateStageTone = (pipeline: PipelineView | undefined, stage: unknown) => {
  const known = pipeline?.stages.find((st) => st.name === stage);
  return known ? stageTone(known.terminalKind) : undefined;
};

export function candidateApiSource(
  conditions: FilterCondition[],
  definitions: readonly FieldDefinitionView[],
): RecordSource<CandidateRecord> {
  const sortable = [
    'name',
    ...CANDIDATE_SORTABLE,
    ...definitions
      .filter((d) => d.isIndexed && FIELD_TYPE_CATALOGUE[d.type].sort !== 'none')
      .map((d) => `custom.${d.key}`),
  ];
  // An unfilled row stays on screen but is never sent: the API answers 400 to it.
  const filter = completeFilter(conditions.length ? { and: conditions } : undefined);
  return {
    key: `candidates:${JSON.stringify(filter ?? null)}:${definitions.map((d) => d.key).join(',')}`,
    sortable,
    async fetchPage({ cursor, limit, sort, filter: text }) {
      const body: CandidateSearchRequest = {
        limit,
        ...(text.trim() ? { q: text.trim() } : {}),
        ...(filter ? { filter } : {}),
        ...(sort
          ? { sort: [{ field: apiSortField(sort.id), dir: sort.desc ? 'desc' : 'asc' }] }
          : {}),
        ...(cursor ? { cursor } : {}),
      };
      const page = await api.candidates.search(body);
      return { items: page.items as CandidateRecord[], nextCursor: page.nextCursor };
    },
  };
}

export function CandidatesPage() {
  const session = useSession();
  const qc = useQueryClient();
  const [settings] = useSettings(session.me.user.id);
  const visible = useVisibleDefinitions('candidates');
  const role = session.me.organisation.role;
  const [editing, setEditing] = useState<CandidateRecord | 'new' | null>(null);
  const [history, setHistory] = useState<CandidateRecord | null>(null);
  // Ticked rows, for Add to job (TS-1599). There is no bulk edit on this screen.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  const [notice, setNotice] = useState<string | null>(null);
  /** The ticked candidates being put on a job's shortlist (TS-1599). */
  const [addToJob, setAddToJob] = useState<string[] | null>(null);
  const shortlistPipeline = usePipeline('shortlist');
  // A candidate's stage is its place in the default Candidate Flow pipeline: its stage names.
  const candidateFlow = usePipeline('candidate');
  const canUpdate = session.can('candidate.update');
  const canAddToJob = session.can('job.update');
  const canUpload = canUpdate && session.can('document.upload');
  const canDownload = session.can('document.read');

  // Inline edit (RELAY-942): one PATCH per cell, applied to every cached page first so the
  // cell never flickers back; a refusal restores the old value and says why.
  const patchCell = useCallback(
    async (id: string, body: CandidateInput, apply: (c: CandidateRecord) => CandidateRecord) => {
      const key = ['records'] as const;
      const snapshot = qc.getQueriesData<unknown>({ queryKey: key });
      const patchPage = (data: unknown): unknown => {
        if (!data || typeof data !== 'object') return data;
        const d = data as {
          pages?: Array<{ items: CandidateRecord[] }>;
          items?: CandidateRecord[];
        };
        const mapItems = (items: CandidateRecord[]) =>
          items.map((c) => (c.id === id ? apply(c) : c));
        if (d.pages)
          return { ...d, pages: d.pages.map((p) => ({ ...p, items: mapItems(p.items) })) };
        if (d.items) return { ...d, items: mapItems(d.items) };
        return data;
      };
      qc.setQueriesData({ queryKey: key }, patchPage);
      try {
        await api.candidates.patch(id, body);
        setNotice(null);
      } catch (err) {
        for (const [k, data] of snapshot) qc.setQueryData(k, data);
        setNotice(describeError(err));
      } finally {
        await qc.invalidateQueries({ queryKey: ['candidate-history', id] });
      }
    },
    [qc],
  );

  // ── Table state: the URL is the source of truth (RELAY-942) ─────────────────────────────
  // `?view=<base64url json>` carries columns + filter + sort + q, so a link reproduces the list;
  // `useListView` restores the last one, or the default saved view, when the URL has none.
  const search = useSearch({ from: '/authed/candidates' });
  const navigate = useNavigate({ from: '/candidates' });
  const write = useCallback(
    (encoded: string) =>
      void navigate({
        search: (prev) => ({
          ...(encoded === encodeTableState(EMPTY_TABLE_STATE) ? {} : { view: encoded }),
          ...(prev.open ? { open: prev.open } : {}),
        }),
        replace: true,
      }),
    [navigate],
  );
  const {
    state: baseState,
    setState,
    activeViewId,
  } = useListView({
    entity: 'candidates',
    remember: 'candidates',
    encoded: search.view,
    write,
  });
  // The side panel (RELAY-944): `?open=<id>` beside the view, never instead of it.
  const setOpen = useCallback(
    (id: string | null) =>
      void navigate({
        search: (prev) => {
          const { open: _o, ...rest } = prev;
          return id ? { ...rest, open: id } : rest;
        },
        replace: true,
      }),
    [navigate],
  );
  const openId = search.open ?? null;
  const openCandidate = useQuery({
    queryKey: ['candidate', openId],
    queryFn: () => api.candidates.get(openId!),
    enabled: openId !== null,
  });
  const customIds = useMemo(() => visible.map((d) => `custom.${d.key}`), [visible]);
  // Custom columns start hidden: one shows only while it is in the view's order and not hidden.
  const state = useMemo<TableState>(() => {
    const { order, hidden } = baseState.columns;
    const implicit = customIds.filter((id) => !order.includes(id) && !hidden.includes(id));
    return implicit.length
      ? { ...baseState, columns: { ...baseState.columns, hidden: [...hidden, ...implicit] } }
      : baseState;
  }, [baseState, customIds]);
  const conditions = useMemo<FilterCondition[]>(
    () =>
      state.filter && 'and' in state.filter
        ? state.filter.and.filter((c): c is FilterCondition => 'field' in c)
        : state.filter && 'field' in state.filter
          ? [state.filter]
          : [],
    [state.filter],
  );
  const setConditions = useCallback(
    (next: FilterCondition[]) =>
      setState(
        next.length ? { ...baseState, filter: { and: next } } : { ...baseState, filter: undefined },
      ),
    [setState, baseState],
  );

  const source = useMemo(() => candidateApiSource(conditions, visible), [conditions, visible]);
  // A different filter is a different set of rows: never carry a selection across it.
  useEffect(() => clearSelection(), [source.key, state.q, clearSelection]);
  const columns = useMemo<ColumnDef<CandidateRecord, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (r) => candidateName(r),
        header: 'Name',
        size: 200,
        cell: ({ row }) => (
          <NameCell
            candidate={row.original}
            canEdit={canUpdate}
            onCommit={(patch) => patchCell(row.original.id, patch, (r) => ({ ...r, ...patch }))}
          />
        ),
      },
      ...STANDARD_COLUMNS.map<ColumnDef<CandidateRecord, unknown>>((c) => ({
        id: c.key,
        accessorFn: (r) => r[c.key],
        header: c.label,
        size: c.size,
        meta: { autoSize: c.autoSize },
        cell: ({ getValue, row }) => {
          const cell = (
            <EditableCell
              type={CANDIDATE_FIELDS[c.key as keyof typeof CANDIDATE_FIELDS] ?? 'text'}
              value={getValue()}
              options={
                c.key === 'stage'
                  ? candidateStageOptions(candidateFlow, row.original.stage)
                  : undefined
              }
              format={c.key === 'expectedHourlyRate' ? HOURLY_RATE_FORMAT : undefined}
              tone={
                c.key === 'stage'
                  ? candidateStageTone(candidateFlow, getValue())
                  : DATE_PILLS.has(c.key)
                    ? dateTone(getValue())
                    : undefined
              }
              label={c.label}
              canEdit={canUpdate && c.editable}
              onCommit={(v) =>
                patchCell(row.original.id, { [c.key]: v }, (r) => ({
                  ...r,
                  [c.key]: v,
                }))
              }
            />
          );
          const flag = c.key === 'country' ? flagForCountry(getValue()) : '';
          return flag ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <span aria-hidden>{flag}</span>
              <span className="min-w-0 flex-1">{cell}</span>
            </span>
          ) : (
            cell
          );
        },
      })),
      {
        id: 'resume',
        accessorFn: (r) => r.resumeDocument?.filename ?? r.resumeFilename,
        header: 'Resume',
        size: 72,
        meta: { autoSize: false },
        enableSorting: false,
        cell: ({ row }) => (
          <ResumeCell
            candidate={row.original}
            canUpload={canUpload}
            canDownload={canDownload}
            onChanged={() => qc.invalidateQueries({ queryKey: ['records'] })}
            onError={setNotice}
          />
        ),
      },
      ...visible.map<ColumnDef<CandidateRecord, unknown>>((d) => ({
        id: `custom.${d.key}`,
        accessorFn: (r) => r.custom?.[d.key],
        header: d.label,
        size: 140,
        cell: ({ getValue, row }) => (
          <EditableCell
            type={d.type}
            value={getValue()}
            options={d.options}
            format={d.format}
            label={d.label}
            canEdit={canUpdate && (d.visibility[role] ?? 'edit') === 'edit'}
            onCommit={(v) =>
              patchCell(row.original.id, { custom: { [d.key]: v } }, (r) => ({
                ...r,
                custom: { ...(r.custom ?? {}), [d.key]: v },
              }))
            }
          />
        ),
      })),
    ],
    [visible, role, canUpdate, canUpload, canDownload, patchCell, qc, candidateFlow],
  );
  // Showing ONE custom column from the Columns menu puts it in the order, which keeps it shown;
  // Reset clears several at once and so returns to the default (customs hidden).
  const onTableStateChange = useCallback(
    (next: TableState) => {
      const shown = customIds.filter(
        (id) =>
          state.columns.hidden.includes(id) &&
          !next.columns.hidden.includes(id) &&
          !next.columns.order.includes(id),
      );
      if (shown.length !== 1 || next.columns.hidden.length !== state.columns.hidden.length - 1) {
        setState(next);
        return;
      }
      const order = next.columns.order.length ? next.columns.order : columns.map((c) => c.id!);
      setState({
        ...next,
        columns: { ...next.columns, order: [...new Set([...order, ...shown])] },
      });
    },
    [customIds, state.columns.hidden, columns, setState],
  );
  const filterFields = useMemo(
    () =>
      candidateFilterFields({
        definitions: visible,
        search: api.candidates.search,
        pipeline: candidateFlow,
      }),
    [visible, candidateFlow],
  );

  const exportCsv = useCallback(async () => {
    try {
      // The view's visible columns in its order (RELAY-947); the Resume column is not a field.
      const ids = columns.map((c) => c.id!).filter((id) => id !== 'resume');
      const ordered = [
        ...state.columns.order.filter((id) => ids.includes(id)),
        ...ids.filter((id) => !state.columns.order.includes(id)),
      ]
        .filter((id) => !state.columns.hidden.includes(id))
        .flatMap((id) => (id === 'name' ? ['firstName', 'lastName'] : [id]));
      const filter = completeFilter(conditions.length ? { and: conditions } : undefined);
      const csv = await api.candidates.exportCsv({
        ...(filter ? { filter } : {}),
        ...(state.q ? { q: state.q } : {}),
        ...(state.sort ? { sort: [{ ...state.sort, field: apiSortField(state.sort.field) }] } : {}),
        ...(ordered.length ? { columns: ordered } : {}),
      });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: 'candidates.csv',
      });
      a.click();
      URL.revokeObjectURL(url);
      setNotice(`Exported ${Math.max(csv.trim().split('\n').length - 1, 0)} rows`);
    } catch (err) {
      setNotice(describeError(err));
    }
  }, [conditions, state.q, state.sort, state.columns, columns]);

  return (
    <Gate permission="candidate.read" feature="candidates.enabled">
      <PageHeader
        title="Candidates"
        description="Everyone in the talent pool: search, filter, and open a record."
        actions={
          <>
            {notice && (
              <span className="text-muted-foreground text-sm" role="status">
                {notice}
              </span>
            )}
            <ViewsMenu
              entity="candidates"
              state={state}
              activeId={activeViewId}
              onApply={(next, id) => setState(next, id)}
            />
            <Button variant="outline" asChild>
              <a href="/candidates/duplicates" data-testid="open-duplicates">
                Duplicates
              </a>
            </Button>
            <Button variant="outline" onClick={() => void exportCsv()} data-testid="export-csv">
              <Download /> Export CSV
            </Button>
            {session.can('candidate.create') && (
              <Button onClick={() => setEditing('new')} data-testid="new-candidate">
                <Plus /> New candidate
              </Button>
            )}
          </>
        }
      />
      {canAddToJob && selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onAddToJob={() => setAddToJob([...selected])}
          onClear={clearSelection}
        />
      )}
      <RecordTable
        columns={columns}
        source={source}
        getRowId={(r) => r.id}
        mode={settings.pagination}
        pageSize={settings.pageSize}
        filterPlaceholder="Search name, email or role…"
        toolbar={
          <FilterControls fields={filterFields} conditions={conditions} onChange={setConditions} />
        }
        tableState={state}
        onTableStateChange={onTableStateChange}
        {...(canAddToJob ? { selection: selected, onSelectionChange: setSelected } : {})}
        onRowClick={(r: CandidateRecord) => setOpen(r.id)}
      />
      <Sheet open={openId !== null} onOpenChange={(o) => !o && setOpen(null)}>
        {openId !== null && (
          <SheetContent data-testid="candidate-panel">
            <SheetTitle className="sr-only">Candidate</SheetTitle>
            <SheetDescription className="sr-only">
              Details of the selected candidate.
            </SheetDescription>
            {openCandidate.data ? (
              <CandidateDetail
                candidate={openCandidate.data}
                definitions={visible}
                fullPageHref={`/candidates/${openId}`}
                onEdit={() => setEditing(openCandidate.data)}
                onChanged={async () => {
                  await qc.invalidateQueries({ queryKey: ['candidate', openId] });
                  await qc.invalidateQueries({ queryKey: ['records'] });
                }}
              />
            ) : openCandidate.error != null ? (
              <p className="text-danger text-sm" role="alert">
                {describeError(openCandidate.error)}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">Loading…</p>
            )}
          </SheetContent>
        )}
      </Sheet>
      {addToJob !== null && (
        <AddToJobDialog
          candidateIds={addToJob}
          label={`${addToJob.length} candidate${addToJob.length === 1 ? '' : 's'}`}
          pipeline={shortlistPipeline}
          onClose={() => setAddToJob(null)}
          onDone={async (result, jobTitle) => {
            setAddToJob(null);
            clearSelection();
            setNotice(describeAdd(result, jobTitle));
            await qc.invalidateQueries({ queryKey: ['candidate-jobs'] });
          }}
        />
      )}
      {history !== null && (
        <HistoryPanel
          candidateId={history.id}
          title={candidateName(history) || 'candidate'}
          onClose={() => setHistory(null)}
        />
      )}
      {editing !== null && (
        <CandidateForm
          candidate={editing === 'new' ? null : editing}
          definitions={visible}
          role={role}
          onHistory={
            editing === 'new'
              ? undefined
              : () => {
                  setHistory(editing);
                  setEditing(null);
                }
          }
          onUploaded={() => void qc.invalidateQueries({ queryKey: ['records'] })}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await qc.invalidateQueries({ queryKey: ['records'] });
            if (openId) await qc.invalidateQueries({ queryKey: ['candidate', openId] });
          }}
        />
      )}
    </Gate>
  );
}

/** First and last name in one cell; double-click edits both, Enter or leaving commits. */
function NameCell({
  candidate,
  canEdit,
  onCommit,
}: {
  candidate: CandidateRecord;
  canEdit: boolean;
  onCommit: (patch: Pick<CandidateInput, 'firstName' | 'lastName'>) => void;
}) {
  const [draft, setDraft] = useState<{ firstName: string; lastName: string } | null>(null);
  const committed = useRef(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const editing = draft !== null;
  useEffect(() => {
    if (editing) editorRef.current?.querySelector('input')?.focus();
  }, [editing]);
  const start = () => {
    if (!canEdit) return;
    committed.current = false;
    setDraft({ firstName: candidate.firstName ?? '', lastName: candidate.lastName ?? '' });
  };
  const commit = () => {
    if (committed.current || !draft) return;
    committed.current = true;
    setDraft(null);
    const patch: Pick<CandidateInput, 'firstName' | 'lastName'> = {};
    for (const key of ['firstName', 'lastName'] as const) {
      const next = draft[key].trim() || null;
      if (next !== (candidate[key] ?? null)) patch[key] = next;
    }
    if (Object.keys(patch).length) onCommit(patch);
  };
  if (draft)
    return (
      <div
        ref={editorRef}
        className="flex w-full gap-1"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            committed.current = true;
            setDraft(null);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) commit();
        }}
        data-testid="cell-editor"
      >
        <Input
          aria-label="First name"
          value={draft.firstName}
          onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
        />
        <Input
          aria-label="Last name"
          value={draft.lastName}
          onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
        />
      </div>
    );
  const name = candidateName(candidate);
  return (
    <div
      className={canEdit ? 'w-full cursor-text truncate' : 'w-full truncate'}
      tabIndex={canEdit ? 0 : undefined}
      onDoubleClick={(e) => {
        if (!canEdit) return;
        e.stopPropagation();
        start();
      }}
      onKeyDown={(e) => {
        if (canEdit && (e.key === 'Enter' || e.key === 'F2')) {
          e.preventDefault();
          start();
        }
      }}
      data-testid="cell-value"
      aria-label={canEdit ? 'Name (double-click to edit)' : undefined}
    >
      {name || <span className="text-muted-foreground">—</span>}
    </div>
  );
}
